/**
 * Writing Craft Radar Chart — 10-category spider chart using Recharts.
 * Enhanced with gold-filled radar, pulsing low-score dots, center score,
 * click-to-expand detail panels, and smooth entrance animation.
 */
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip,
} from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useMemo } from 'react';

const CATEGORY_LABELS = {
  authenticity: 'Authenticity',
  originality: 'Originality',
  voice_consistency: 'Voice',
  subtext_density: 'Subtext',
  economy: 'Economy',
  tonal_control: 'Tone',
  sensory_immersion: 'Sensory',
  emotional_honesty: 'Honesty',
  wit_intelligence: 'Wit',
  rhythm_pacing: 'Rhythm',
};

function scoreColor(score) {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
}

/* Pulsing red glow keyframes injected once */
const PULSE_STYLE_ID = 'wc-radar-pulse-style';
function ensurePulseStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PULSE_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PULSE_STYLE_ID;
  style.textContent = `
    @keyframes wc-pulse-red {
      0%, 100% { filter: drop-shadow(0 0 2px rgba(239,68,68,0.4)); }
      50%      { filter: drop-shadow(0 0 8px rgba(239,68,68,0.9)); }
    }
    .wc-pulse-dot { animation: wc-pulse-red 2s ease-in-out infinite; }
  `;
  document.head.appendChild(style);
}

/** Custom dot renderer — gold circles, pulsing red glow when score < 60 */
function CustomDot(props) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  const isLow = payload?.score < 60;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={5}
      fill="#C9A84C"
      stroke="#C9A84C"
      strokeWidth={2}
      className={isLow ? 'wc-pulse-dot' : ''}
      style={isLow ? {} : { filter: 'drop-shadow(0 0 3px rgba(201,168,76,0.6))' }}
    />
  );
}

/** Custom axis tick — allows clicking a category label */
function ClickableTick({ payload, x, y, textAnchor, onCategoryClick, selectedCategory }) {
  const isActive = selectedCategory === payload?.value;
  return (
    <g
      onClick={() => onCategoryClick?.(payload?.value)}
      style={{ cursor: 'pointer' }}
    >
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        fill={isActive ? '#C9A84C' : 'var(--text-muted)'}
        fontSize={10}
        fontWeight={isActive ? 700 : 400}
      >
        {payload?.value}
      </text>
    </g>
  );
}

export default function WritingCraftRadar({ data }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [animProgress, setAnimProgress] = useState(0);

  useEffect(() => { ensurePulseStyle(); }, []);

  // Entrance animation: radar fills from center over 1s
  useEffect(() => {
    if (!data?.categories) return;
    let frame;
    const start = performance.now();
    const duration = 1000;
    function tick(now) {
      const t = Math.min((now - start) / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimProgress(eased);
      if (t < 1) frame = requestAnimationFrame(tick);
    }
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [data]);

  const { strongest, weakest } = useMemo(() => {
    if (!data?.categories?.length) return { strongest: null, weakest: null };
    const sorted = [...data.categories].sort((a, b) => b.score - a.score);
    return {
      strongest: CATEGORY_LABELS[sorted[0].name] || sorted[0].name,
      weakest: CATEGORY_LABELS[sorted[sorted.length - 1].name] || sorted[sorted.length - 1].name,
    };
  }, [data]);

  // Find expanded detail data when a category is clicked
  const selectedDetail = useMemo(() => {
    if (!selectedCategory || !data?.categories) return null;
    return data.categories.find(
      (c) => (CATEGORY_LABELS[c.name] || c.name) === selectedCategory,
    );
  }, [selectedCategory, data]);

  if (!data?.categories) return null;

  const chartData = data.categories.map((c) => ({
    category: CATEGORY_LABELS[c.name] || c.name,
    score: Math.round(c.score * animProgress),
    rawScore: c.score,
    fullMark: 100,
  }));

  const handleCategoryClick = (label) => {
    setSelectedCategory((prev) => (prev === label ? null : label));
  };

  return (
    <div className="space-y-4">
      {/* Header with overall score */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Writing Craft</h3>
        <span className={`text-lg font-bold ${scoreColor(data.overall_score)}`}>
          {data.overall_score}/100
        </span>
      </div>

      {/* Subtitle */}
      {strongest && weakest && (
        <p className="text-xs text-[var(--text-muted)]">
          Strongest: <span className="text-emerald-400 font-medium">{strongest}</span>
          {' · '}
          Weakest: <span className="text-red-400 font-medium">{weakest}</span>
        </p>
      )}

      {/* Radar Chart with center score */}
      <div className="relative h-72 rounded-xl" style={{ backgroundColor: '#09090B' }}>
        {/* Center overall score overlay */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ zIndex: 10 }}
        >
          <span
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '2rem',
              fontWeight: 700,
              color: '#C9A84C',
              textShadow: '0 0 20px rgba(201,168,76,0.4)',
            }}
          >
            {data.overall_score}
          </span>
        </div>

        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={chartData} cx="50%" cy="50%" outerRadius="70%">
            <PolarGrid stroke="#222228" />
            <PolarAngleAxis
              dataKey="category"
              tick={
                <ClickableTick
                  onCategoryClick={handleCategoryClick}
                  selectedCategory={selectedCategory}
                />
              }
            />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
            <Radar
              dataKey="score"
              stroke="#C9A84C"
              fill="rgba(201,168,76,0.3)"
              fillOpacity={1}
              strokeWidth={2}
              dot={<CustomDot />}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#15151A',
                border: '1px solid #222228',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#E5E5E5',
              }}
              formatter={(value, name, entry) => {
                // Show real score, not animated
                return [entry?.payload?.rawScore ?? value, 'Score'];
              }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Detail panel when a radar axis category is clicked */}
      <AnimatePresence>
        {selectedDetail && (
          <motion.div
            key={selectedCategory}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="rounded-lg border border-[#C9A84C]/30 bg-[var(--bg-elevated)] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold" style={{ color: '#C9A84C' }}>
                  {CATEGORY_LABELS[selectedDetail.name] || selectedDetail.name}
                </h4>
                <span className={`text-sm font-bold ${scoreColor(selectedDetail.score)}`}>
                  {selectedDetail.score}/100
                </span>
              </div>

              {/* Voice / panelist assessment */}
              {selectedDetail.assessment && (
                <p className="text-xs text-[var(--text-secondary)]">
                  {selectedDetail.assessment}
                </p>
              )}

              {/* Exemplar quote with page reference */}
              {selectedDetail.exemplar_quote && (
                <div className="pl-3 border-l-2 border-[#C9A84C]/40">
                  <p className="text-xs text-[var(--text-muted)] italic">
                    "{selectedDetail.exemplar_quote}"
                  </p>
                  {selectedDetail.page_ref && (
                    <p className="text-[10px] text-[var(--text-muted)] mt-1">
                      — p. {selectedDetail.page_ref}
                    </p>
                  )}
                </div>
              )}

              {/* Strengths as exemplar quotes (backward compat) */}
              {selectedDetail.strengths?.length > 0 && (
                <div>
                  <span className="text-emerald-400 font-medium text-xs">Strengths:</span>
                  {selectedDetail.strengths.map((s, j) => (
                    <div key={j} className="mt-1 pl-3 border-l-2 border-emerald-500/30">
                      <p className="text-xs text-[var(--text-muted)] italic">"{s.line}"</p>
                      <p className="text-xs text-[var(--text-secondary)] mt-0.5">{s.explanation}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Suggestion if score < 70 */}
              {selectedDetail.score < 70 && (selectedDetail.suggestion || selectedDetail.weaknesses?.length > 0) && (
                <div className="rounded-md bg-red-500/5 border border-red-500/20 p-3">
                  <span className="text-red-400 font-medium text-xs">Suggestion:</span>
                  {selectedDetail.suggestion && (
                    <p className="text-xs text-[var(--text-secondary)] mt-1">{selectedDetail.suggestion}</p>
                  )}
                  {selectedDetail.weaknesses?.map((w, j) => (
                    <div key={j} className="mt-1 pl-3 border-l-2 border-red-500/30">
                      <p className="text-xs text-[var(--text-muted)] italic">"{w.line}"</p>
                      <p className="text-xs text-[var(--text-secondary)] mt-0.5">{w.explanation}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Category Details (collapsible list — backward compatible) */}
      <div className="space-y-1">
        {data.categories.map((cat, i) => {
          const label = CATEGORY_LABELS[cat.name] || cat.name;
          const isOpen = selectedCategory === label;
          return (
            <div key={cat.name} className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)]">
              <button
                onClick={() => handleCategoryClick(label)}
                className="w-full flex items-center justify-between px-3 py-2 text-left"
              >
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${scoreColor(cat.score)}`}>
                    {cat.score}
                  </span>
                  <span className="text-xs text-[var(--text-secondary)]">
                    {label}
                  </span>
                  {cat.score < 60 && (
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  )}
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
